import { useDispatch } from 'react-redux';

import {
  requestMfaProcedureSuccess,
  requestTotpProcedureSuccess,
} from 'common/components/ModalMFA/actions';
import { useQueryError } from 'common/components/QueryError/useQueryError';
import { useTranslation } from 'common/hooks/useTranslation';
import { requestScaProcedureSuccess } from 'modules/StrongCustomerAuthentication/redux/actions';
import { useMutation } from 'src/core/api/hooks/useMutation';
import { type QueryError } from 'src/core/api/queryError';

import { type Quote } from '../../../models';
import { type TransferId } from '../../models';

export type AuthType = 'sca' | 'mfa' | 'totp';

export type VopMappingByTransferId = Record<TransferId, string>;

type ConfirmTransfersPayload = {
  transferIds: TransferId[];
  vopMappingByTransferId?: VopMappingByTransferId;
  quotes: Quote[];
  authType?: 'totp' | 'mfa';
  endpointParams: {
    shouldCallV2: boolean;
  };
};

export type ConfirmationError = {
  wtRequestId: string;
  transferSchedulingPaymentId: TransferId;
  error: string;
  errorDetails?: string;
};

export type ConfirmTransferErrors =
  | {
      transferId: TransferId;
      reason:
        | 'walletAmountInsufficient'
        | 'alreadyCancelled'
        | 'alreadyConfirmed'
        | 'supplierNotFound'
        | 'supplierBankInfoInvalid'
        | 'notScreened'
        | 'screeningPending'
        | 'screeningBlocked';
      error?: string;
    }
  | {
      reason:
        | 'forbidden'
        | 'bankingProviderNotSupportedForInvoiceWireTransfers'
        | 'accountOwnerIsMissingMfaPhoneFactor'
        | 'unableToCreateScaProcedure';
    }
  | {
      reason: typeof GBP_BATCH_CONFIRMATION_NOT_ALLOWED_REASON;
      errors: ConfirmationError[];
    };

export const GBP_BATCH_CONFIRMATION_NOT_ALLOWED_REASON =
  'batchConfirmNotAllowedForGbpWireTransfer';

export const GBP_BATCH_CONFIRMATION_MAX_RETRIES = 10;

type ConfirmTransferResponse =
  | {
      authType: 'mfa';
      factorId: string;
      procedureId: string;
    }
  | {
      authType: 'sca';
      procedureId: string;
    }
  | {
      authType: 'totp';
      factorId: string;
      procedureId: string;
    };

export const useConfirmTransfers = () => {
  const dispatch = useDispatch();

  return useMutation<
    ConfirmTransfersPayload,
    ConfirmTransferResponse,
    ConfirmTransferResponse,
    ConfirmTransferErrors
  >({
    request: {
      type: 'rest',
      target: 'companyAPI',
      endpoint: ({ shouldCallV2 }) =>
        `/transfer-scheduling/transfers/confirmation${shouldCallV2 ? '?version=2' : ''}`,
      method: 'post',
    },
    options: {
      throwOnError: true,
      onSuccess: ({ data }): void => {
        switch (data.authType) {
          case 'totp': {
            dispatch(
              requestTotpProcedureSuccess({
                id: data.procedureId,
                factorId: data.factorId,
              }),
            );

            break;
          }
          case 'mfa': {
            dispatch(
              requestMfaProcedureSuccess({
                id: data.procedureId,
                factorId: data.factorId,
              }),
            );

            break;
          }
          case 'sca': {
            dispatch(
              requestScaProcedureSuccess({
                procedureId: data.procedureId,
              }),
            );

            break;
          }
          default:
            break;
        }
      },
    },
    reshapeData: (data) => data,
  });
};

type TranslationsParams = {
  count: number;
};

export const useConfirmTransferErrorMessage = () => {
  const { t } = useTranslation('global');
  const getErrorMessage = useQueryError<
    ConfirmTransferErrors,
    TranslationsParams
  >({
    requestError: ({ reason }) => {
      switch (reason) {
        case 'walletAmountInsufficient':
          return 'invoices.transfer.confirm.walletAmountInsufficientError';

        case 'forbidden':
          return 'invoices.transfer.confirm.forbiddenError';

        case 'alreadyCancelled':
          return 'invoices.transfer.confirm.alreadyCancelledError';

        case 'alreadyConfirmed':
          return 'invoices.transfer.confirm.alreadyConfirmedError';

        case 'supplierNotFound':
          return 'invoices.transfer.confirm.supplierNotFoundError';

        case 'supplierBankInfoInvalid':
          return 'invoices.transfer.confirm.supplierBankInfoInvalidError';

        case 'bankingProviderNotSupportedForInvoiceWireTransfers':
          return 'invoices.transfer.confirm.bankingProviderNotSupportedForInvoiceWireTransfersError';

        case 'accountOwnerIsMissingMfaPhoneFactor':
        case 'unableToCreateScaProcedure':
          return 'invoices.transfer.confirm.accountOwnerIsMissingMfaPhoneFactorError';

        case 'notScreened':
        case 'screeningPending':
        case 'screeningBlocked':
          return 'invoices.transfer.confirm.userNotScreenedError';

        default:
          return 'invoices.transfer.confirm.error';
      }
    },
    serverError: 'invoices.transfer.confirm.error',
  });

  const shouldDisplayErrorNotification = (
    queryError: QueryError<ConfirmTransferErrors>,
    translationsParams: TranslationsParams,
  ) => {
    return (
      getErrorMessage(queryError, translationsParams) !==
      t('invoices.transfer.confirm.accountOwnerIsMissingMfaPhoneFactorError')
    );
  };

  const getMessage = (
    queryError: QueryError<ConfirmTransferErrors>,
    translationsParams: TranslationsParams,
  ): string => getErrorMessage(queryError, translationsParams);

  return { getMessage, shouldDisplayErrorNotification };
};
